'use client'
import type { FC, ReactNode, Dispatch, SetStateAction } from 'react'
import { createContext, useCallback, useEffect, useMemo, useState } from 'react'
import { useRouter } from 'next/navigation'
import { serverCookieRemoveByMaxAge } from '@/app/actions'
import { DY_COOKIE_DOMAIN, DY_COOKIE_PAYMENT_METHOD, DY_COOKIE_SESSION } from '@/utils'
import type { TypeCmsCustomer } from '@/schema'
import { GET_CUSTOMER } from '@/api/graphql/customer'
import { useQuery } from '@apollo/client'

export type TypeSessionCustomer = {
  bigcommerceId?: TypeCmsCustomer['bigcommerceId']
  email: TypeCmsCustomer['email']
  firstName?: TypeCmsCustomer['firstName']
  id: TypeCmsCustomer['id']
  lastName?: TypeCmsCustomer['lastName']
  paymentMethod?: TypeCmsCustomer['paymentMethod']
}

type TypeState = {
  user: TypeSessionCustomer
  setUser: Dispatch<SetStateAction<TypeState['user']>>
  closeSession: () => void
	resetSession: () => void
  customerToken: string | null
  setCustomerToken?: Dispatch<SetStateAction<string | null>>
  sessionChecked: boolean
  isAuthenticated: boolean
}

const initialState: TypeState = {
  user: null,
  setUser: () => {},
  closeSession: () => {},
	resetSession: () => {},
  customerToken: null,
  sessionChecked: false,
  isAuthenticated: false,
}

export const SessionContext = createContext<TypeState | null>(initialState)

interface Props {
  children: ReactNode
  initialCustomerToken?: string | null
  initialUser?: TypeCmsCustomer | null
}

export const SessionProvider:FC<Props> = ({ children, initialCustomerToken = null, initialUser = null }) => {
  const [user, setUser] = useState<TypeState['user']>(initialUser ?? undefined)
  const [customerToken, setCustomerToken] = useState<TypeState['customerToken']>(initialCustomerToken ?? null)
  const [sessionChecked, setSessionChecked] = useState<TypeState['sessionChecked']>(!initialCustomerToken || !!initialUser)
  const [loggingOut, setLoggingOut] = useState<boolean>(false)
  const { push } = useRouter()

  const setPreferredPaymentMethod = useCallback((paymentMethod: string) => {
    if (typeof document === 'undefined') return
    const secure = process.env.NODE_ENV === 'production' ? '; Secure' : ''
    document.cookie = `${DY_COOKIE_PAYMENT_METHOD}=${encodeURIComponent(paymentMethod)}; Domain=${DY_COOKIE_DOMAIN}; Path=/; SameSite=Lax${secure}`
  }, [])

  useEffect(() => {
    if (user?.paymentMethod) setPreferredPaymentMethod(user.paymentMethod)
  }, [user, setPreferredPaymentMethod])

  const handleCustomerLoaded = useCallback((customer: (TypeCmsCustomer & { paymentMethod?: string }) | null | undefined) => {
    if (loggingOut || user || !customer) return
    setUser(customer)
    setSessionChecked(true)
  }, [loggingOut, user])

  useQuery(GET_CUSTOMER, {
    skip: !customerToken || (customerToken && !!user),
		fetchPolicy: 'no-cache',
    context: {
      privateStore: true,
      DYCustomerToken: customerToken
    },
    onCompleted: (queryData) => {
      handleCustomerLoaded(queryData?.customer)
    },
    onError: () => {
      setSessionChecked(true)
    },
  })

	const resetSession = useCallback(() => {
		setCustomerToken(null)
		setUser(null)
		setSessionChecked(true)
	}, [])

  const closeSession = useCallback(async () => {
    setLoggingOut(true)
    await serverCookieRemoveByMaxAge(DY_COOKIE_SESSION)
    await serverCookieRemoveByMaxAge(DY_COOKIE_PAYMENT_METHOD)
    setCustomerToken(null)
		setUser(null)
		setSessionChecked(true)
    push('/')

    setTimeout(() => {
      setLoggingOut(false)
    }, 350)
  }, [push])

  const isAuthenticated = sessionChecked && !!customerToken

  const contextValue = useMemo(() => ({
    user,
    setUser,
    closeSession,
		resetSession,
    customerToken,
    setCustomerToken,
    sessionChecked,
    setSessionChecked,
    isAuthenticated,
  }), [
    user,
    closeSession,
    customerToken,
    sessionChecked,
    isAuthenticated,
    resetSession,
  ])

  return <SessionContext.Provider value={contextValue}>{children}</SessionContext.Provider>
}
